import { ChangeDetectionStrategy, Component, computed, EventEmitter, OnChanges, Output, signal, SimpleChanges, ViewEncapsulation, input, output } from '@angular/core';
import { Activity, DateUtility, Job, ShareableEntity } from '@jms/model';
import { DateTime } from 'luxon';
import { EmptyMessageModule } from '@jms/ui/ui.empty.message';

import { AccordionComponent, AccordionGroupComponent } from '@workbuddy/ui/components/accordion';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { ActivityContext, ActivityDisplayMode, ActivityGroup, ActivityListItem } from '../../model/model';
import { ActivityItemComponent } from '../activity.item/activity.item.component';
import * as Enumerable from 'linq';
import { v7 } from 'uuid';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'activity-list',
  templateUrl: './activity.list.component.html',
  styleUrls: ['./activity.list.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    AccordionComponent,
    AccordionGroupComponent,
    CdkAccordionModule,
    ActivityItemComponent,
    EmptyMessageModule
  ]
})
export class ActivityListComponent {

  protected activityGroups: ActivityGroup;
  protected ActivityDisplayMode = ActivityDisplayMode;

  public readonly $context = input<ActivityContext>(null, { alias: 'context' });
  public readonly $selectedActivity = input<Activity>(null, { alias: 'selectedActivity' });
  public readonly $activities = input<Activity[]>([], { alias: 'activities' });
  public readonly $job = input<Job>(null, { alias: 'job' });

  public selected = output<Activity>();
  public edit = output<Activity>();
  public delete = output<Activity>();
  public share = output<ShareableEntity>();

  protected selectedId = null;
  protected expandedThreads = signal(new Set<string>());
  protected localThreadResolvedState = signal(new Map<string, boolean>());

  protected $groups = computed(() => {

    const activities = this.$activities();
    const context = this.$context();
    const expandedThreads = this.expandedThreads();
    const localThreadResolvedState = this.localThreadResolvedState();

    if (!activities?.length || !context) {
      return [];
    }

    // Partition into top-level rows (thread roots + standalone) and thread replies keyed by their root.
    const childrenByThread = new Map<string, Activity[]>();
    const topLevel: Activity[] = [];
    for (const a of activities) {
      if (a.threadId && a.threadId !== a._id) {
        const list = childrenByThread.get(a.threadId) ?? [];
        list.push(a);
        childrenByThread.set(a.threadId, list);
      } else {
        topLevel.push(a);
      }
    }

    // Replies read oldest → newest within a thread; top-level rows newest → oldest.
    for (const list of childrenByThread.values()) {
      list.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
    }
    topLevel.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());

    // Build one row per top-level activity, attaching each thread root its replies.
    // Resolved threads collapse to just the last reply (unless the user expanded them).
    const listItems: ActivityListItem[] = topLevel.map(activity => {

      const isRoot = !!activity.threadId && activity.threadId === activity._id;
      const children = isRoot ? childrenByThread.get(activity._id) ?? [] : [];

      if (!children.length) {
        return { activity, threadChildren: [], hiddenChildCount: 0, threadCollapsible: false };
      }

      // Local resolve state takes precedence over stale input data.
      const isResolved = localThreadResolvedState.has(activity._id)
        ? localThreadResolvedState.get(activity._id)
        : activity.threadResolved;
      const isExpanded = expandedThreads.has(activity._id);
      // Only resolved threads with more than one reply collapse — so only they carry the expand/collapse toggle.
      const collapsible = !!isResolved && children.length > 1;

      if (collapsible && !isExpanded) {
        return { activity, threadChildren: [children[children.length - 1]], hiddenChildCount: children.length - 1, threadCollapsible: true };
      }

      return { activity, threadChildren: children, hiddenChildCount: 0, threadCollapsible: collapsible };
    });

    const groups = Enumerable
      .from(listItems)
      .select(item => {
        // Group by the top-level activity's own date; replies now live inside their root's card.
        const groupDate = DateUtility.getDateTime(item.activity.createdDate, context.timezone);
        return { item, date: groupDate.toLocaleString(DateTime.DATE_FULL) };
      })
      .groupBy(x => x.date)
      .select(x => {
        const entries = x.toArray();
        const items = entries.map(e => e.item);
        return {
          _id: v7(),
          date: entries[0].date,
          firstItem: items[0].activity,
          items,
          expanded: true
        } as ActivityGroup;
      })
      .toArray();

    // Sort groups by most recent top-level activity date (descending).
    groups.sort((a, b) => new Date(b.firstItem.createdDate).getTime() - new Date(a.firstItem.createdDate).getTime());

    return groups;
  });

  protected toggleThread(threadId: string) {

    const updated = new Set(this.expandedThreads());
    if (updated.has(threadId)) {
      updated.delete(threadId);
    } else {
      updated.add(threadId);
    }
    this.expandedThreads.set(updated);
  }

  protected onThreadResolvedChange(activity: Activity) {

    if (!activity?.threadId) return;
    const state = new Map(this.localThreadResolvedState());
    state.set(activity.threadId, !!activity.threadResolved);
    this.localThreadResolvedState.set(state);

    // Clear from expanded set so re-resolving collapses the thread again
    if (activity.threadResolved && this.expandedThreads().has(activity.threadId)) {
      const updated = new Set(this.expandedThreads());
      updated.delete(activity.threadId);
      this.expandedThreads.set(updated);
    }
  }


  protected onSelected(activity: Activity) {

    if (!activity) {
      return;
    }

    const selected = this.$selectedActivity();

    if (selected?._id === activity?._id) {
      return;
    }

    this.selected.emit(activity);
  }

  protected toggle(group: ActivityGroup) {
    group.expanded = !group.expanded;
  }

}
